<template>
  <div>
    <references-tab
      :review="review"
      :references="references"
      :highlights="highlights"
      :total-count="totalCount"
      :researcher="researcher"
      :filtering-input="filteringInput"
      :loading="loadingReferences"
      :loading-more="loadingMore"
      :has-next-page="hasNextPage"
      :disable-actions-for-references="disableActionsForReferences"
      @load-more="onLoadMore"
      @update:filteringInput="onFilteringInputUpdate"
      @edit="onEditReference"
      @download-full-text="onDownloadFullText"
      @delete-full-text="onDeleteFullText"
      @set-reference-tags="setReferenceTags"
      @create-screening-vote="onSaveReviewerScreeningVote"
      @revoke-screening-vote="onDeleteReviewerScreeningVote"
      @change-screening-vote="onSaveReviewerScreeningVote"
      @resolve-screening-conflict="onResolveScreeningConflict"
      @change-screening-outcome="onChangeScreeningOutcome"
      @create-full-text-review-vote="onSaveFullTextReviewVote"
      @change-full-text-review-vote="onSaveFullTextReviewVote"
      @revoke-full-text-review-vote="onDeleteFullTextReviewVote"
      @resolve-full-text-review-conflict="onSaveFullTextReviewOutcome"
      @change-full-text-review-outcome="onSaveFullTextReviewOutcome"
      @extract="onExtractReference"
      @extraction-consensus-review="onExtractionConsensusReview"
      @mark-as-duplicate="openDuplicateModal"
    />
    <reference-form-modal
      :reference="editReference"
      :focus-field="editReferenceField"
      @save="onSaveReference"
      @cancel="onCancelEditReference"
    />
    <not-relevant-review-modal
      v-model="showNotRelevantReviewModal"
      :review="review"
      :hide-notes="hideNotesInNotRelevantReviewModal"
      @save="onSaveNotRelevantReview"
    />
    <mark-as-duplicate-modal
      v-if="duplicateModalReference"
      :value="duplicateModalOpen"
      :reference="duplicateModalReference"
      @input="duplicateModalOpen = $event"
      @marked="onDuplicateMarked"
    />
    <v-snackbar
      :value="!!duplicateSnackbar"
      :timeout="-1"
      @input="duplicateSnackbar = null"
    >
      <template v-if="duplicateSnackbar">
        Merged &ldquo;{{ duplicateSnackbar.referenceTitle }}&rdquo;
        into &ldquo;{{ duplicateSnackbar.originalTitle }}&rdquo;
      </template>
      <template #action="{ attrs }">
        <v-btn text v-bind="attrs" @click="onUndoDuplicateMerge">
          Undo
        </v-btn>
        <v-btn icon v-bind="attrs" @click="duplicateSnackbar = null">
          <v-icon>close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import ReferencesTab from './ReferencesTab';
import ReferenceFormModal from '@/components/ReferenceForm/ReferenceFormModal';
import NotRelevantReviewModal from '@/components/FullTextReviewPage/NotRelevantReviewModal';
import MarkAsDuplicateModal from '@/components/References/MarkAsDuplicateModal';
import {
  Review,
  Researcher,
  ReferenceConnection,
  ReferenceFilteringInputDto,
  FullTextReviewVote,
  FullTextReviewOutcome,
} from '@/models';
import { InclusionAssessment } from '@/enums';
import {
  GetCurrentResearcher,
  GetReferencesByFilters,
  GetReferenceDetails,
} from '@/graphql';
import { isEqual } from 'lodash';
import { mapActions } from 'vuex';

// Increased page size for better performance with large datasets
const PAGE_SIZE = 50;
// Use larger page size for subsequent loads
const LOAD_MORE_SIZE = 100;

export default {
  name: 'ReferencesTabController',
  components: {
    ReferencesTab,
    ReferenceFormModal,
    NotRelevantReviewModal,
    MarkAsDuplicateModal,
  },
  props: {
    review: {
      type: Review,
      required: true,
    },
  },
  data: () => ({
    researcher: null,
    referencesByFilters: null,
    filteringInput: new ReferenceFilteringInputDto(),
    editReference: null,
    editReferenceField: null,
    showNotRelevantReviewModal: false,
    hideNotesInNotRelevantReviewModal: false,
    pendingExcludeAssessment: null,
    disableActionsForReferences: [],
    detailedReferences: {}, // Cache for detailed reference data
    duplicateModalOpen: false,
    duplicateModalReference: null,
    duplicateSnackbar: null,
  }),
  apollo: {
    researcher: {
      query: GetCurrentResearcher,
      update: (data) => new Researcher(data.currentResearcher),
    },
    referencesByFilters: {
      query: GetReferencesByFilters,
      variables() {
        return {
          reviewId: this.review.id,
          filters: this.filteringInput.asInput(),
          first: PAGE_SIZE,
        };
      },
      debounce: 500,
      update: (data) =>
        new ReferenceConnection(data.referencesByFilters),
      fetchPolicy: 'network-only',
    },
  },
  computed: {
    references() {
      return this.referencesByFilters?.edges.map((edge) => edge.node);
    },
    highlights() {
      return this.referencesByFilters?.pageInfo.highlights;
    },
    totalCount() {
      return this.referencesByFilters?.pageInfo.totalCount;
    },
    loadingReferences() {
      return !this.referencesByFilters;
    },
    loadingMore() {
      return (
        (this.referencesByFilters &&
          this.$apollo.queries.referencesByFilters.loading) ??
        false
      );
    },
    hasNextPage() {
      return this.referencesByFilters?.pageInfo.hasNextPage;
    },
  },
  methods: {
    ...mapActions({
      updateReferenceFields: 'reference/updateReferenceFields',
      saveReviewerScreeningVote:
        'reviewerScreeningVote/saveAndAwaitRefetch',
      deleteReviewerScreeningVote: 'reviewerScreeningVote/delete',
      resolveScreeningConflict: 'screeningOutcome/resolveConflict',
      saveScreeningOutcome: 'screeningOutcome/save',
      saveFullTextReviewVote:
        'fullTextReviewVote/saveAndAwaitRefetch',
      deleteFullTextReviewVote: 'fullTextReviewVote/delete',
      saveFullTextReviewOutcome: 'fullTextReviewOutcome/save',
      fetchDownloadUrl: 'fullTextPdf/fetchDownloadUrl',
      deleteFullTextPdf: 'fullTextPdf/delete',
      setReferenceTags: 'reference/setReferenceTags',
      unmarkAsDuplicate: 'reference/unmarkAsDuplicate',
    }),
    onFilteringInputUpdate(newInput) {
      // If the filtering input is the same as the previous one, do nothing.
      if (isEqual(newInput, this.filteringInput)) return;
      this.filteringInput = newInput;

      // Clear the previous results so the correct loading state is shown.
      this.referencesByFilters = null;
      // Clear detailed reference cache on filter change
      this.detailedReferences = {};
    },
    onLoadMore() {
      if (!this.referencesByFilters?.pageInfo?.hasNextPage) return;
      if (this.loadingMore) return;

      this.$apollo.queries.referencesByFilters.fetchMore({
        variables: {
          first: LOAD_MORE_SIZE, // Use larger size for subsequent loads
          filters: this.filteringInput.asInput(),
          after: this.referencesByFilters.pageInfo.endCursor,
        },
        updateQuery: this.mergePreviousAndNextResults,
      });
    },
    mergePreviousAndNextResults(previousResult, { fetchMoreResult }) {
      const keyWithEdges = Object.keys(previousResult).find(
        (key) => !!previousResult[key].edges
      );
      return {
        ...previousResult,
        [keyWithEdges]: {
          ...previousResult[keyWithEdges],
          ...fetchMoreResult[keyWithEdges],
          pageInfo: {
            ...previousResult[keyWithEdges].pageInfo,
            ...fetchMoreResult[keyWithEdges].pageInfo,
            highlights: [
              ...previousResult[keyWithEdges].pageInfo.highlights,
              ...fetchMoreResult[keyWithEdges].pageInfo.highlights,
            ],
          },
          edges: [
            ...previousResult[keyWithEdges].edges,
            ...fetchMoreResult[keyWithEdges].edges,
          ],
        },
      };
    },
    async loadDetailedReference(referenceId) {
      // Check cache first
      if (this.detailedReferences[referenceId]) {
        return this.detailedReferences[referenceId];
      }

      const result = await this.$apollo.query({
        query: GetReferenceDetails,
        variables: { referenceId },
        fetchPolicy: 'network-only',
      });

      // Cache the detailed data
      this.detailedReferences[referenceId] = result.data.reference;
      return result.data.reference;
    },
    enableActions(reference) {
      this.disableActionsForReferences = this.disableActionsForReferences.filter(
        (id) => id !== reference.id
      );
    },
    disableActions(reference) {
      this.disableActionsForReferences.push(reference.id);
    },
    async disableActionsDuringOp(assessment, op) {
      if (!assessment.reference) {
        throw new Error('Assessment must have a reference');
      }
      try {
        this.disableActions(assessment.reference);
        await op(assessment);
        // The mutation changed this reference's votes/outcomes — the cached
        // detail snapshot is stale.
        this.invalidateDetailedReference(assessment.reference.id);
      } finally {
        this.enableActions(assessment.reference);
      }
    },
    invalidateDetailedReference(referenceId) {
      this.$delete(this.detailedReferences, referenceId);
    },
    async onEditReference({ reference, focus }) {
      // Load detailed data before editing
      const detailed = await this.loadDetailedReference(reference.id);
      this.editReference = detailed;
      this.editReferenceField = focus;
    },
    onCancelEditReference() {
      this.editReference = null;
      this.editReferenceField = null;
    },
    onSaveReference(reference) {
      this.updateReferenceFields(reference);
      // Drop the cached pre-edit snapshot or reopening the editor
      // serves stale data and can silently revert this save.
      this.invalidateDetailedReference(reference.id);
      this.editReference = null;
      this.editReferenceField = null;
    },
    onSaveReviewerScreeningVote(voteToSave) {
      this.disableActionsDuringOp(
        voteToSave,
        this.saveReviewerScreeningVote
      );
    },
    onDeleteReviewerScreeningVote(voteToDelete) {
      this.disableActionsDuringOp(
        voteToDelete,
        this.deleteReviewerScreeningVote
      );
    },
    onResolveScreeningConflict(conflictToResolve) {
      this.disableActionsDuringOp(
        conflictToResolve,
        this.resolveScreeningConflict
      );
    },
    onChangeScreeningOutcome(outcomeToChange) {
      this.disableActionsDuringOp(
        outcomeToChange,
        this.saveScreeningOutcome
      );
    },
    onSaveFullTextReviewVote(voteToSave) {
      if (voteToSave.vote !== InclusionAssessment.EXCLUDE) {
        return this.disableActionsDuringOp(
          voteToSave,
          this.saveFullTextReviewVote
        );
      }
      this.triggerShowNotRelevantReviewModal(voteToSave, false);
    },
    onDeleteFullTextReviewVote(voteToDelete) {
      this.disableActionsDuringOp(
        voteToDelete,
        this.deleteFullTextReviewVote
      );
    },
    onSaveFullTextReviewOutcome(outcomeToSave) {
      if (outcomeToSave.outcome !== InclusionAssessment.EXCLUDE) {
        return this.disableActionsDuringOp(
          outcomeToSave,
          this.saveFullTextReviewOutcome
        );
      }
      this.triggerShowNotRelevantReviewModal(outcomeToSave, true);
    },
    triggerShowNotRelevantReviewModal(assessment, hideNotes) {
      this.pendingExcludeAssessment = assessment;
      this.showNotRelevantReviewModal = true;
      this.hideNotesInNotRelevantReviewModal = hideNotes;
    },
    onSaveNotRelevantReview({ reviewCriteria, notes }) {
      const assessment = this.pendingExcludeAssessment;
      if (!assessment) {
        return;
      }
      const isVote = assessment instanceof FullTextReviewVote;
      if (isVote) {
        const voteToSave = new FullTextReviewVote({
          ...assessment,
          reasonReviewCriteria: reviewCriteria,
          notes,
        });
        this.disableActionsDuringOp(
          voteToSave,
          this.saveFullTextReviewVote
        );
      } else {
        const outcomeToSave = new FullTextReviewOutcome({
          ...assessment,
          reasonReviewCriteria: reviewCriteria,
          notes,
        });
        this.disableActionsDuringOp(
          outcomeToSave,
          this.saveFullTextReviewOutcome
        );
      }
      this.pendingExcludeAssessment = null;
    },
    async onDownloadFullText(reference) {
      const url = await this.fetchDownloadUrl({
        referenceId: reference.id,
        forDownload: true,
      });
      const a = document.createElement('a');
      a.href = url;
      a.click();
    },
    async onDeleteFullText(reference) {
      await this.deleteFullTextPdf(reference.id);
    },
    onExtractReference(reference) {
      this.$router.push(
        `/review/${this.review.id}/data-extraction?show=${reference.id}`
      );
    },
    onExtractionConsensusReview(reference) {
      this.$router.push(
        `/review/${this.review.id}/consensus-review?show=${reference.id}`
      );
    },
    openDuplicateModal(reference) {
      this.duplicateModalReference = reference;
      this.duplicateModalOpen = true;
    },
    onDuplicateMarked(merge) {
      this.duplicateModalOpen = false;
      this.duplicateSnackbar = merge;
    },
    async onUndoDuplicateMerge() {
      const merge = this.duplicateSnackbar;
      if (!merge) return;
      await this.unmarkAsDuplicate({
        referenceId: merge.referenceId,
      });
      this.duplicateSnackbar = null;
    },
  },
};
</script>
